import { ComponentProps, HTMLAttributes, Ref } from 'react';
import { DayPicker, getDefaultClassNames } from 'react-day-picker';

import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon } from '@/assets/icons';
import { Button, BUTTON_VARIANTS } from '@/components/common';
import { mergeClasses } from '@/utils';

import {
  CalendarDayButton,
  CustomFooter,
  MonthsDropdown,
  YearsDropdown,
} from '@/components/common/Calendar/components';

const Calendar = ({
  className,
  classNames,
  showOutsideDays = true,
  captionLayout = 'label',
  buttonVariant = 'secondary',
  formatters,
  components,
  ...props
}: ComponentProps<typeof DayPicker> & {
  buttonVariant?: ComponentProps<typeof Button>['variant'];
}) => {
  const defaultClassNames = getDefaultClassNames();

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={mergeClasses(
        'bg-neutral-white group/calendar px-m py-base [--cell-size:--spacing(8)] [[data-slot=card-content]_&]:bg-transparent [[data-slot=popover-content]_&]:bg-transparent',
        String.raw`rtl:**:[.rdp-button\_next>svg]:rotate-180`,
        String.raw`rtl:**:[.rdp-button\_previous>svg]:rotate-180`,
        className,
      )}
      captionLayout={captionLayout}
      formatters={{
        formatWeekdayName: (weekday) => weekday.toLocaleString('default', { weekday: 'short' }),
        ...formatters,
      }}
      classNames={{
        root: mergeClasses('w-fit', defaultClassNames.root),
        months: mergeClasses('flex gap-4 flex-col md:flex-row relative', defaultClassNames.months),
        month: mergeClasses('flex flex-col w-full gap-4', defaultClassNames.month),
        nav: mergeClasses(
          'flex items-center gap-1 absolute right-0 top-0 justify-end [&_button]:p-none [&_button]:!min-w-l [&_button]:!min-h-l [&_button]:w-l [&_button]:h-l [&_button:hover]:bg-neutral-n3',
          defaultClassNames.nav,
        ),
        button_previous: mergeClasses(
          BUTTON_VARIANTS({ variant: buttonVariant }),
          'border-none !shadow-none aria-disabled:opacity-50 p-0 select-none',
          defaultClassNames.button_previous,
        ),
        button_next: mergeClasses(
          BUTTON_VARIANTS({ variant: buttonVariant }),
          'border-none !shadow-none aria-disabled:opacity-50 p-0 select-none',
          defaultClassNames.button_next,
        ),
        month_caption: mergeClasses(
          'flex items-center justify-center h-(--cell-size) w-full px-(--cell-size)',
          defaultClassNames.month_caption,
        ),
        dropdowns: mergeClasses(
          'w-full flex items-center text-sm font-medium h-(--cell-size) gap-1',
          defaultClassNames.dropdowns,
        ),
        dropdown_root: mergeClasses(
          'relative has-focus:border-ring border border-input shadow-xs has-focus:ring-ring/50 has-focus:ring-[3px] rounded-md',
          defaultClassNames.dropdown_root,
        ),
        dropdown: mergeClasses('absolute bg-popover inset-0 opacity-0', defaultClassNames.dropdown),
        caption_label: mergeClasses(
          'select-none font-medium',
          captionLayout === 'label'
            ? 'text-sm'
            : 'rounded-md pl-2 pr-1 flex items-center gap-1 text-sm h-8 [&>svg]:text-muted-foreground [&>svg]:size-3.5',
          defaultClassNames.caption_label,
        ),
        table: 'w-full border-collapse',
        weekdays: mergeClasses('flex', defaultClassNames.weekdays),
        weekday: mergeClasses(
          'text-muted-foreground rounded-md flex-1 font-normal text-[0.8rem] select-none',
          defaultClassNames.weekday,
        ),
        week: mergeClasses('flex w-full mt-2', defaultClassNames.week),
        week_number_header: mergeClasses(
          'select-none w-(--cell-size)',
          defaultClassNames.week_number_header,
        ),
        week_number: mergeClasses(
          'text-[0.8rem] select-none text-muted-foreground',
          defaultClassNames.week_number,
        ),
        day: mergeClasses(
          'relative w-full h-full p-0 text-center group/day aspect-square select-none',
          defaultClassNames.day,
        ),
        range_start: mergeClasses(
          'shadow-[inset_-20px_0_0_0_#e9ecef]',
          defaultClassNames.range_start,
        ),
        range_middle: mergeClasses('rounded-none', defaultClassNames.range_middle),
        range_end: mergeClasses('shadow-[inset_20px_0_0_0_#e9ecef]', defaultClassNames.range_end),
        today: mergeClasses(
          '[&_button]:border [&_button]:border-solid [&_button]:border-neutral-n4 text-accent-foreground rounded-md data-[selected=true]:rounded-none',
          defaultClassNames.today,
        ),
        outside: mergeClasses(
          '[&_button]:text-neutral-n6 aria-selected:text-muted-foreground',
          defaultClassNames.outside,
        ),
        disabled: mergeClasses('text-muted-foreground opacity-50', defaultClassNames.disabled),
        hidden: mergeClasses('invisible', defaultClassNames.hidden),
        ...classNames,
      }}
      components={{
        Root: ({
          className,
          rootRef,
          ...props
        }: {
          rootRef?: Ref<HTMLDivElement> | undefined;
        } & HTMLAttributes<HTMLDivElement>) => {
          return (
            <div
              data-slot="calendar"
              ref={rootRef}
              className={mergeClasses(className)}
              {...props}
            />
          );
        },
        Chevron: ({
          className,
          orientation,
          ...props
        }: {
          className?: string | undefined;
          size?: number | undefined;
          disabled?: boolean | undefined;
          orientation?: 'left' | 'right' | 'up' | 'down' | undefined;
        }) => {
          if (orientation === 'left') {
            return <ChevronLeftIcon className={mergeClasses('size-4', className)} {...props} />;
          }

          if (orientation === 'right') {
            return <ChevronRightIcon className={mergeClasses('size-4', className)} {...props} />;
          }

          return <ChevronDownIcon className={mergeClasses('size-4', className)} {...props} />;
        },
        DayButton: CalendarDayButton,
        WeekNumber: ({ children, ...props }) => {
          return (
            <td {...props}>
              <div className="flex size-(--cell-size) items-center justify-center text-center">
                {children}
              </div>
            </td>
          );
        },
        YearsDropdown: YearsDropdown,
        MonthsDropdown: MonthsDropdown,
        Footer: CustomFooter,
        ...components,
      }}
      {...props}
    />
  );
};

export { Calendar };
